import { useEffect, useMemo, useState } from 'react';
import type { Profile } from '@/types/db';
import type { TimelineEventWithExtras } from '@/features/timeline/lib/timelineQueries';
import {
  computeProfileFacts,
  rankSuggestions,
  readClickLedger,
  recordSuggestionClick,
  type RankedSuggestion,
} from '@/features/chat/lib/suggestions';

interface SuggestionChipsProps {
  profile: Profile;
  events: TimelineEventWithExtras[];
  disabled?: boolean;
  onSelect: (text: string) => void;
}

export function SuggestionChips({
  profile,
  events,
  disabled = false,
  onSelect,
}: SuggestionChipsProps) {
  // Bumping this counter on click forces re-rank so a tapped chip sinks
  // immediately rather than reappearing on the next render.
  const [refresh, setRefresh] = useState(0);
  // Re-pick rotation/tiebreak each chat-page mount so suggestions feel fresh,
  // but stay stable across re-renders within the same view.
  const [seed] = useState(() => Date.now());

  const ranked = useMemo<RankedSuggestion[]>(() => {
    const facts = computeProfileFacts(profile, events);
    const ledger = readClickLedger(profile.id);
    return rankSuggestions({
      profile,
      facts,
      ledger,
      now: Date.now(),
    });
    // `seed` and `refresh` are intentional triggers — see comments above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile, events, seed, refresh]);

  // Re-read the ledger when the page is foregrounded — covers cross-tab clicks.
  useEffect(() => {
    function onVisibility() {
      if (document.visibilityState === 'visible') {
        setRefresh((r) => r + 1);
      }
    }
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  if (ranked.length === 0) return null;

  function handleTap(text: string) {
    if (disabled) return;
    recordSuggestionClick(profile.id, text);
    setRefresh((r) => r + 1);
    onSelect(text);
  }

  return (
    <div
      className="flex gap-2 overflow-x-auto -mx-1 px-1 pb-2 mb-2"
      aria-label="Suggested questions"
    >
      {ranked.map((s) => (
        <button
          key={s.text}
          type="button"
          onClick={() => handleTap(s.text)}
          disabled={disabled}
          className="shrink-0 min-h-tap px-3 rounded-full border border-border bg-accent-soft/60 text-accent text-sm font-medium hover:bg-accent-soft disabled:opacity-50"
        >
          {s.text}
        </button>
      ))}
    </div>
  );
}
